/**
 * Idea invalidations join the server-resolved project and global rooms the
 * caller may read Ideas in. A frame names the Idea and nothing of its content,
 * so a subscriber reads the Idea again through the API when one arrives.
 */

import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'
import type { IdeaEventPayload } from '@/types/idea'

/** Every event the Ideas service emits, as `contracts/work_items/idea_event.py` spells it. */
export const IDEA_EVENTS = [
  'idea_created',
  'idea_updated',
  'idea_claimed',
  'idea_state_changed',
  'idea_entry_posted',
  'idea_entry_edited',
  'idea_entry_resolved',
  'idea_decision_proposed',
  'idea_decision_accepted',
  'idea_decision_rejected',
  'idea_participant_changed',
  'idea_work_linked',
  'idea_deleted',
] as const

export class IdeasLiveApi {
  private readonly changes: SubscriptionRegistry<null, IdeaEventPayload>

  constructor(private readonly client: WebSocketClient) {
    this.changes = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_ideas', {}),
      leave: () => this.client.emitWhenConnected('unsubscribe_ideas', {}),
    })
  }

  subscribe(callback: (payload: IdeaEventPayload) => void): () => void {
    this.attachHandlers()
    return this.changes.subscribe(null, callback)
  }

  private attachHandlers(): void {
    if (!this.client.hasSocket()) return
    for (const event of IDEA_EVENTS) {
      this.client.attachOnce(event, (raw) =>
        this.changes.dispatch(null, raw as IdeaEventPayload)
      )
    }
  }

  replay(): void {
    this.attachHandlers()
    this.changes.replay()
  }

  clear(): void {
    this.changes.clear()
  }
}
